import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Button, DataTable, DefinitionList, Drawer, EmptyState, Inline, Link, PageHeader, Panel, Skeleton, Stack, StatusDot, t, Text, toast,
} from '@kanbios/ui';
import type { fleetState, maintenance } from '@kanbios/api-client';
import { Shell } from '../../components/Shell';
import { OutsideScopeBanner, useDisplayScope } from '../../components/ScopePicker';
import { RefusalPage } from '../../components/Refusal';
import { EnvironmentBadge, ServerStateBadge, serverStateColumn } from '../../components/ServerState';
import { EnvironmentBadges, ObligationBadges } from '../../components/Inventory';
import { AddressTable } from '../../components/domains/AddressTable';
import { PullRequestNotice } from '../../components/catalogue/PullRequestNotice';
import { QualificationDialog } from '../../components/catalogue/QualificationDialog';
import { ProjectTeam } from '../../components/catalogue/ProjectTeam';
import { api, unwrap } from '../../lib/api';
import { shortDate, shortVersion } from '../../lib/format';
import { useSession } from '../../lib/session';
import { useEnvironmentName } from '../../lib/environments';
import { ProjectEnvironments } from '../../components/ProjectEnvironments';
import { FULL_HEIGHT } from '../../lib/tables';

type Project = maintenance.components['schemas']['ProjectDetail'];
type Matrix = fleetState.components['schemas']['ProjectMatrix'];
type Row = Matrix['rows'][number];
type Cell = fleetState.components['schemas']['MatrixCell'];

/**
 * Project page (SPEC-005), DSM « Record with tabs » (LayoutRecordBand): the
 * band says which project and which section; the overview is the matrix
 * applications × environments, a cell opens its deployment in a Drawer.
 */
export function ProjectPage() {
  const envName = useEnvironmentName();
  const { project = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { enums } = useSession();
  const scope = useDisplayScope();
  const tab = params.get('tab') ?? 'overview';
  const highlighted = params.get('application');
  const [qualifying, setQualifying] = useState(false);

  const detail = useQuery({
    queryKey: ['project', project, scope],
    queryFn: async () => unwrap(await api.maintenance.GET('/projects/{project}', { params: { path: { project }, query: { scope } } })),
    refetchInterval: 30_000,
    retry: false,
  });
  const matrix = useQuery({
    queryKey: ['project-matrix', project],
    queryFn: async () => unwrap(await api.fleetState.GET('/projects/{project}/matrix', { params: { path: { project } } })),
    enabled: detail.isSuccess,
    refetchInterval: 30_000,
  });

  // RG-005-4 and its refusal: an application of another project shows the whole matrix, said once.
  useEffect(() => {
    if (highlighted && matrix.data && !matrix.data.rows.some(r => r.application === highlighted)) {
      toast({ title: t('project.applicationElsewhere'), tone: 'warn' });
      const next = new URLSearchParams(params);
      next.delete('application');
      setParams(next, { replace: true });
    }
  }, [highlighted, matrix.data]);

  // SPEC-034 § 9: the deployments the platform installed — their version is the one it deployed, even before the
  // agent reports it; each one has its own page.
  const installed = useQuery({
    queryKey: ['environments', project],
    queryFn: async () => unwrap(await api.maintenance.GET('/environments', { params: { query: { project } } })),
    enabled: detail.isSuccess,
    retry: false,
  });
  const installedOf = (deployment: string) => installed.data?.items.find(x => x.deployment === deployment);
  const versionOf = (cell: Cell) => cell.version ?? (installedOf(cell.deployment)?.release ? shortVersion(installedOf(cell.deployment)!.release!) : null);
  const typeOf = (env: string) => matrix.data?.environments.find(x => x.name === env)?.type ?? null;

  // The open deployment lives in the URL (?deployment=), so a cell is a link that can be shared.
  const openDeployment = params.get('deployment');
  const open = (() => {
    for (const row of matrix.data?.rows ?? []) {
      for (const [env, cell] of Object.entries(row.cells)) if (cell && cell.deployment === openDeployment) return { row, env, cell };
    }
    return null;
  })();
  const deploymentHref = (deployment: string | null) => {
    const next = new URLSearchParams(params);
    if (deployment) next.set('deployment', deployment);
    else next.delete('deployment');
    return next;
  };

  // The team's rights (the same query as its tab: one request).
  const team = useQuery({
    queryKey: ['project-team', project],
    queryFn: async () => unwrap(await api.maintenance.GET('/projects/{project}/team', { params: { path: { project } } })),
    enabled: tab === 'team',
  });
  const [editingTeam, setEditingTeam] = useState(false);
  const crumbs = [{ label: t('nav.projects'), href: '/projects' }, { label: detail.data?.title ?? project }];
  if (detail.error) return <Shell crumbs={crumbs}><RefusalPage error={detail.error} /></Shell>;
  const p = detail.data;
  const label = (list: { value: string; label: string }[], value: string) => list.find(i => i.value === value)?.label ?? value;
  const canManage = p?.actions.includes('project.servers.update') ?? false;

  const header = (
    <PageHeader
      band
      eyebrow={t('page.kind.project')}
      title={p?.title ?? project}
      status={p ? (p.state ? <ServerStateBadge state={p.state} /> : undefined) : undefined}
      subtitle={p ? [p.client.title, t('project.team', { team: p.team }), t('project.criticality', { criticality: label(enums.criticalities, p.criticality) }), ...p.obligations.map(o => label(enums.obligations, o))].join(' · ') : undefined}
      tabs={{
        label: t('project.tabs.label'),
        value: tab,
        onChange: id => setParams(id === 'overview' ? {} : { tab: id }),
        items: (p?.tabs ?? ['overview', 'servers', 'settings']).map(id => ({ id, label: t(`project.tabs.${id}`) })),
        // SPEC-005, DSM Tabs: the action of the active tab sits at the end of the tab bar, and follows the tab.
        action: (tab === 'overview' || tab === 'servers') && canManage
          ? <Button size="sm" icon="dns" onClick={() => navigate(`/projects/${encodeURIComponent(project)}/servers`)}>{t('project.manageServers')}</Button>
          : tab === 'team' && team.data?.canEdit
            ? <Button size="sm" icon="group" onClick={() => setEditingTeam(true)}>{t('team.edit')}</Button>
            : undefined,
      }}
    />
  );

  const cellButton = (row: Row, env: string) => {
    const cell = row.cells[env];
    if (!cell) return <Text variant="body-sm" tone="mute">{t('project.matrix.notDeployed')}</Text>;
    // SPEC-034 § 12.4: a retired deployment (volumes kept, no server) says so, not « Inconnu » with its last version.
    const installedState = installedOf(cell.deployment)?.state;
    const retired = Boolean(installedState && ['retire_requested', 'retiring', 'unlinking', 'retired', 'purging', 'resetting', 'purged'].includes(installedState));
    const state = retired ? null : enums.states.find(s => s.value === cell.state);
    const stateLabel = retired ? t(`environment.state.${installedState}`) : state?.label ?? cell.state;
    const next = deploymentHref(cell.deployment);
    const where = cell.servers.length ? cell.servers.map(s => s.name).join(', ') : t('project.matrix.noServer');
    // Two lines: state and version, then where it runs and its address.
    return (
      <Stack gap={4}>
      <Inline gap={8} align="center" wrap={false}>
        <StatusDot tone={state?.tone ?? 'neutral'} variant={state?.tone === 'neutral' || retired ? 'ring' : 'solid'} label={stateLabel} tooltip={false} />
        {/* The link's name starts with its visible text (WCAG 2.5.3), then says which deployment. */}
        <Link
          href={`?${next.toString()}`}
          onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); setParams(next); }}
          aria-label={t('project.matrix.open', { text: stateLabel, application: row.application, environment: envName(env) })}
        >
          {stateLabel}
        </Link>
        {!retired && <Text variant="caption" tone="mute" truncate>{versionOf(cell) ?? t('project.matrix.noVersion')}</Text>}
      </Inline>
      <Text variant="caption" tone="mute" truncate>{cell.url ? `${where} · ${cell.url.replace(/^https?:\/\//, '')}` : where}</Text>
      </Stack>
    );
  };

  return (
    <Shell crumbs={crumbs} header={header}>
      {!p ? (
        <Skeleton lines={8} />
      ) : (
        <Stack gap={24}>
          <OutsideScopeBanner kind="project" outside={p.outsideDisplayScope} />
          {tab === 'overview' && (
            <Panel title={t('project.matrix.title')} subtitle={t('project.matrix.subtitle')} flush>
              <DataTable
                bordered={false}
                height={FULL_HEIGHT}
                label={t('project.matrix.title')}
                rows={matrix.data?.rows ?? []}
                rowKey="application"
                loading={matrix.isPending}
                toolbar={false}
                // Two lines per cell (state and version, then servers and address): the default density.
                activeRowKey={highlighted ?? undefined}
                columns={[
                  // SPEC-033: the application's name opens its page.
                  { key: 'application', label: t('project.matrix.application'), pinned: 'left', width: 320, mono: true, render: (r: Row) => (
                    <Link href={`/applications/${encodeURIComponent(r.application)}`} onClick={(e: { preventDefault: () => void; metaKey?: boolean; ctrlKey?: boolean }) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); navigate(`/applications/${encodeURIComponent(r.application)}`); }}>
                      {r.application}
                    </Link>
                  ) },
                  { key: 'role', label: t('project.matrix.role'), width: 140, render: (r: Row) => label(enums.applicationRoles, r.role) },
                  // One column per environment; the last one takes the room left.
                  // D48: an environment by its name, cells keyed by it.
                  ...(matrix.data?.environments ?? []).map(({ name: env }, i, all) => ({
                    // The environment in full (recette → Recette), so the column reads as one.
                    key: `env-${env}`, label: envName(env), width: 340, grow: i === all.length - 1, sortable: false, render: (r: Row) => cellButton(r, env),
                  })),
                ]}
                empty={<EmptyState framed={false} icon="apps" title={t('project.matrix.empty')} />}
              />
            </Panel>
          )}
          {tab === 'servers' && (
            // « Gérer les serveurs » is the tab bar's action, on every tab.
            <Panel title={t('project.servers.title')} subtitle={t('project.servers.subtitle')} flush>
              <DataTable
                bordered={false}
                height={FULL_HEIGHT}
                label={t('project.servers.title')}
                rows={p.servers}
                rowKey="name"
                toolbar={false}
                density="compact"
                onRowClick={(s: Project['servers'][number]) => navigate(`/servers/${encodeURIComponent(s.name)}`)}
                columns={[
                  serverStateColumn<Project['servers'][number]>(enums, s => ({ state: s.state })),
                  { key: 'name', label: t('project.servers.name'), mono: true, width: 220 },
                  { key: 'environment', label: t('project.servers.environment'), width: 140, render: (s: Project['servers'][number]) => (s.environment ? <EnvironmentBadge environment={s.environment} /> : t('inventory.none')) },
                  { key: 'applications', label: t('project.servers.applications'), grow: true, render: (s: Project['servers'][number]) => s.applications.join(', ') },
                  { key: 'clients', label: t('project.servers.placement'), width: 180, render: (s: Project['servers'][number]) => (s.clients >= 2 ? t('project.servers.shared', { n: s.clients }) : t('project.servers.dedicated')) },
                ]}
                empty={<EmptyState framed={false} icon="dns" title={t('project.servers.empty')} />}
              />
            </Panel>
          )}
          {tab === 'domains' && (
            // D27: the project's addresses, and the ones to attach on the servers it shares.
            <Panel title={t('project.domains.title')} subtitle={t('project.domains.subtitle')}>
              <AddressTable project={p.name} />
            </Panel>
          )}
          {tab === 'environments' && <ProjectEnvironments project={p.name} />}
          {tab === 'team' && <ProjectTeam project={{ name: p.name, title: p.title }} editing={editingTeam} onEditClose={() => setEditingTeam(false)} />}
          {tab === 'settings' && <PullRequestNotice pullRequest={p.pullRequests.qualification} />}
          {tab === 'settings' && (
            <Panel
              title={t('project.settings.title')}
              actions={p.actions.includes('project.qualification.update')
                ? <Button variant="secondary" size="sm" onClick={() => setQualifying(true)}>{t('project.settings.editQualification')}</Button>
                : undefined}
            >
              <DefinitionList
                items={[
                  { label: t('project.settings.client'), value: p.client.title },
                  { label: t('project.settings.team'), value: p.team },
                  { label: t('project.settings.criticality'), value: label(enums.criticalities, p.criticality) },
                  { label: t('project.settings.qualification'), value: <ObligationBadges obligations={p.obligations} />, hint: p.qualification?.qualifiedAt ? t('project.settings.qualifiedAt', { date: shortDate(p.qualification.qualifiedAt) }) : t('project.settings.notQualified') },
                  { label: t('project.settings.environments'), value: <EnvironmentBadges environments={p.environments} /> },
                  { label: t('project.settings.repositories'), value: p.repositories.join(', '), mono: true },
                ]}
              />
            </Panel>
          )}
        </Stack>
      )}
      {p && qualifying && (
        <QualificationDialog
          open
          onClose={() => setQualifying(false)}
          project={{ name: p.name, title: p.title }}
          current={{ criticality: p.criticality, healthData: p.qualification?.healthData ?? null, dora: p.qualification?.dora ?? null, sensitivePersonalData: p.qualification?.sensitivePersonalData ?? null, internetExposed: p.qualification?.internetExposed ?? null }}
        />
      )}
      <Drawer
        open={Boolean(open)}
        onClose={() => setParams(deploymentHref(null))}
        eyebrow={t('project.deployment.eyebrow')}
        title={open ? `${open.row.application} · ${envName(open.env)}` : ''}
        size="xl"
      >
        {open && (
          <Stack gap={24}>
            <DefinitionList
              items={[
                { label: t('project.deployment.environment'), value: <EnvironmentBadge environment={open.env} type={typeOf(open.env)} /> },
                { label: t('project.deployment.state'), value: <ServerStateBadge state={open.cell.state} /> },
                { label: t('project.deployment.version'), value: versionOf(open.cell) ?? t('project.matrix.noVersion') },
                // SPEC-034 § 12: an installed deployment has its page (versions, variables, logs, tasks).
                ...(installedOf(open.cell.deployment) ? [{
                  label: t('project.deployment.page'),
                  value: <Link href={`/environments/${encodeURIComponent(open.cell.deployment)}`} onClick={(ev: { preventDefault: () => void; metaKey?: boolean; ctrlKey?: boolean }) => { if (ev.metaKey || ev.ctrlKey) return; ev.preventDefault(); navigate(`/environments/${encodeURIComponent(open.cell.deployment)}`); }}>{open.cell.deployment}</Link>,
                }] : []),
                { label: t('project.deployment.deployedAt'), value: open.cell.deployedAt ? shortDate(open.cell.deployedAt) : null },
                { label: t('project.deployment.url'), value: open.cell.url ? <Link href={open.cell.url} external>{open.cell.url}</Link> : null },
              ]}
            />
            <DataTable
              height={FULL_HEIGHT}
              label={t('project.deployment.servers')}
              rows={open.cell.servers}
              rowKey="name"
              toolbar={false}
              density="compact"
              onRowClick={(s: Cell['servers'][number]) => navigate(`/servers/${encodeURIComponent(s.name)}`)}
              columns={[
                serverStateColumn<Cell['servers'][number]>(enums, s => ({ state: s.state })),
                { key: 'name', label: t('project.servers.name'), mono: true, grow: true },
              ]}
              empty={<EmptyState framed={false} icon="dns" title={t('project.deployment.noServer')} />}
            />
          </Stack>
        )}
      </Drawer>
    </Shell>
  );
}
